import React, { useRef, useState, useMemo, useEffect } from 'react'
import { Camera, ImagePlus, X, ZoomIn } from 'lucide-react'
import {
  Card,
  ChangeImageButton,
  ImageActions,
  ImageBox,
  ImageEmpty,
  ImageLabel,
  ImageOverlay,
  ImagePreview,
  ImagesRow,
  RemoveImageButton,
} from './styles'
import { GeneralFile } from './types'
import ImageLightbox from '../../../../components/ImageViewer'
import AttachmentSourceModal from '../../../../components/AttachmentSourceModal'
import AttachmentGalleryModal, { GalleryFile } from '../../../../components/AttachmentGalleryModal'
import AttachmentApiService from '../../../../services/AttachmentApiService'

export type SlotKey = 'front' | 'side' | 'back'

interface SlotProps {
  label: string
  file: GeneralFile | null
  onRequestChange: () => void
  onRemove?: () => void
  onOpenPreview: (url: string) => void
  disabled?: boolean
}

const ImageSlot: React.FC<SlotProps> = ({
  label,
  file,
  onRequestChange,
  onRemove,
  onOpenPreview,
  disabled,
}) => {
  const [displayUrl, setDisplayUrl] = useState<string>(file?.signedUrl || file?.fileUrl || '')

  useEffect(() => {
    let active = true
    const currentUrl = file?.signedUrl || file?.fileUrl || ''
    if (!currentUrl) {
      setDisplayUrl('')
      return
    }

    if (
      currentUrl.includes('X-Goog-Signature') ||
      currentUrl.includes('X-Amz-Signature') ||
      currentUrl.startsWith('blob:') ||
      currentUrl.startsWith('data:')
    ) {
      setDisplayUrl(currentUrl)
      return
    }

    setDisplayUrl(currentUrl)

    AttachmentApiService.getSignedUrl(currentUrl)
      .then((signed) => {
        if (active && signed) {
          setDisplayUrl(signed)
        }
      })
      .catch((error) => console.error(error))

    return () => {
      active = false
    }
  }, [file?.fileUrl, file?.signedUrl])

  const handleCardClick = () => {
    if (file) {
      onOpenPreview(displayUrl || file.fileUrl)
      return
    }
    if (!disabled) {
      onRequestChange()
    }
  }

  const handleChangeClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled) {
      onRequestChange()
    }
  }

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled && onRemove) {
      onRemove()
    }
  }

  return (
    <ImageBox $hasImage={!!file} $disabled={!!disabled} onClick={handleCardClick}>
      {file ? (
        <>
          <ImagePreview src={displayUrl || file.fileUrl} alt={label} />
          <ImageOverlay className='overlay'>
            <ZoomIn size={22} />
            <span style={{ fontSize: 13, fontWeight: 600 }}>Clique para aumentar</span>
          </ImageOverlay>
          {!disabled && (
            <ImageActions onClick={(e) => e.stopPropagation()}>
              <ChangeImageButton
                type='button'
                onClick={handleChangeClick}
                title='Trocar foto'
              >
                <ImagePlus size={14} />
                <span>Trocar</span>
              </ChangeImageButton>
              {onRemove && (
                <RemoveImageButton
                  type='button'
                  onClick={handleRemoveClick}
                  title='Remover foto'
                >
                  <X size={14} />
                </RemoveImageButton>
              )}
            </ImageActions>
          )}
        </>
      ) : (
        <ImageEmpty>
          <Camera size={28} />
          <span>{disabled ? '—' : 'Adicionar foto'}</span>
        </ImageEmpty>
      )}
      <ImageLabel>{label}</ImageLabel>
    </ImageBox>
  )
}

interface Props {
  front: GeneralFile | null
  side: GeneralFile | null
  back: GeneralFile | null
  onUpload: (slot: SlotKey, file: File) => void
  onSelectGallery?: (slot: SlotKey, file: GalleryFile) => void
  onRemove?: (slot: SlotKey) => void
  disabled?: boolean
}

const ImagesSection: React.FC<Props> = ({
  front,
  side,
  back,
  onUpload,
  onSelectGallery,
  onRemove,
  disabled,
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [isSourceModalOpen, setIsSourceModalOpen] = useState(false)
  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState(false)
  const [activeSlot, setActiveSlot] = useState<{ key: SlotKey; label: string } | null>(null)
  const pendingSlotRef = useRef<{ key: SlotKey; label: string } | null>(null)
  const isChoosingRef = useRef(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const slots = useMemo(
    () => [
      { key: 'front' as SlotKey, label: 'Frente', file: front },
      { key: 'side' as SlotKey, label: 'Lateral', file: side },
      { key: 'back' as SlotKey, label: 'Costas', file: back },
    ],
    [front, side, back],
  )

  const activeSlides = useMemo(() => {
    return slots
      .filter((s): s is typeof s & { file: GeneralFile } => Boolean(s.file?.fileUrl))
      .map((s) => ({
        src: s.file.signedUrl || s.file.fileUrl,
        title: s.label,
      }))
  }, [slots])

  const handleOpenPreview = (targetUrl: string) => {
    const targetIndex = activeSlides.findIndex(
      (s) => s.src === targetUrl || targetUrl.includes(s.src) || s.src.includes(targetUrl),
    )
    setLightboxIndex(Math.max(0, targetIndex))
    setLightboxOpen(true)
  }

  const handleRequestChange = (slot: { key: SlotKey; label: string }) => {
    if (disabled) return
    isChoosingRef.current = false
    pendingSlotRef.current = slot
    setActiveSlot(slot)
    setIsSourceModalOpen(true)
  }

  const handleChooseUpload = () => {
    isChoosingRef.current = true
    setIsSourceModalOpen(false)
    fileInputRef.current?.click()
  }

  const handleChooseGallery = () => {
    isChoosingRef.current = true
    setIsSourceModalOpen(false)
    setIsGalleryModalOpen(true)
  }

  const handleCloseSourceModal = () => {
    setIsSourceModalOpen(false)
    if (!isChoosingRef.current) {
      pendingSlotRef.current = null
      setActiveSlot(null)
    }
  }

  const handleCloseGalleryModal = () => {
    setIsGalleryModalOpen(false)
    isChoosingRef.current = false
    pendingSlotRef.current = null
    setActiveSlot(null)
  }

  const handleSelectGalleryFiles = (selectedFiles: GalleryFile[]) => {
    const slot = pendingSlotRef.current || activeSlot
    if (selectedFiles?.[0] && slot && onSelectGallery) {
      onSelectGallery(slot.key, selectedFiles[0])
    }
    setIsGalleryModalOpen(false)
    isChoosingRef.current = false
    pendingSlotRef.current = null
    setActiveSlot(null)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    const slot = pendingSlotRef.current || activeSlot
    if (file && slot) {
      onUpload(slot.key, file)
    }
    e.target.value = ''
    isChoosingRef.current = false
    pendingSlotRef.current = null
    setActiveSlot(null)
  }

  return (
    <Card>
      <ImagesRow>
        {slots.map((s) => (
          <ImageSlot
            key={s.key}
            label={s.label}
            file={s.file}
            onRequestChange={() => handleRequestChange({ key: s.key, label: s.label })}
            onRemove={onRemove ? () => onRemove(s.key) : undefined}
            onOpenPreview={() => s.file && handleOpenPreview(s.file.fileUrl)}
            disabled={disabled}
          />
        ))}
      </ImagesRow>

      <ImageLightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={activeSlides}
        index={lightboxIndex}
      />

      <AttachmentSourceModal
        isOpen={isSourceModalOpen}
        onClose={handleCloseSourceModal}
        title={activeSlot ? `Foto da Peça - ${activeSlot.label}` : 'Adicionar Foto'}
        description='Escolha como deseja adicionar a foto da peça:'
        uploadLabel='Upload de arquivo'
        uploadDescription='Selecione uma imagem do seu dispositivo'
        galleryLabel='Selecionar da galeria'
        galleryDescription='Escolha uma imagem salva nos anexos da empresa'
        onChooseUpload={handleChooseUpload}
        onChooseGallery={handleChooseGallery}
      />

      <AttachmentGalleryModal
        isOpen={isGalleryModalOpen}
        onClose={handleCloseGalleryModal}
        multiple={false}
        defaultFilter='image'
        title={activeSlot ? `Galeria da Empresa - Foto ${activeSlot.label}` : 'Galeria da Empresa'}
        onSelect={handleSelectGalleryFiles}
      />

      <input
        ref={fileInputRef}
        type='file'
        accept='image/*'
        style={{ display: 'none' }}
        onChange={handleFileChange}
        disabled={disabled}
      />
    </Card>
  )
}

export default ImagesSection
